/**
 * Registro.
 *
 * Es el formulario más largo del producto y por eso está partido en
 * secciones con `<fieldset>`: quien lo recorre con teclado o con lector de
 * pantalla oye en qué parte está, en vez de veinte campos seguidos.
 *
 * Las reglas que no se ven pero mandan:
 *  - la ubicación es en cascada y cambiar arriba limpia lo de abajo;
 *  - el idioma y el tema se aplican en el acto, antes de tener cuenta;
 *  - la confirmación de contraseña no sale de este archivo;
 *  - al enviar bien, la persona queda con la sesión abierta y va a la misma
 *    pantalla a la que llega un login: crear la cuenta ES entrar.
 */
import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
  CATEGORY_LEVELS,
  ID_DOCUMENT_TYPES,
  SEXES,
  SUPPORTED_LOCALES,
  THEMES,
  type Sex,
  type SupportedLocale,
  type Theme,
} from '@tennisup/contracts';
import { PROVINCES, cantonsOfProvince, districtsOfCanton } from '@tennisup/costa-rica-geo';

import { Alert } from '../components/Alert';
import { AuthLayout } from '../components/AuthLayout';
import { ChoiceGroup } from '../components/ChoiceGroup';
import { CountryPicker, type CountryOption } from '../components/CountryPicker';
import { Field } from '../components/Field';
import { ImagePicker } from '../components/ImagePicker';
import { Select } from '../components/Select';
import { PinIcon } from '../components/icons';
import { useDeviceLocation } from '../hooks/useDeviceLocation';
import { useAuth } from '../auth/AuthContext';
import { api } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';
import { countriesSortedFor, countryName, findCountry } from '../lib/countries';
import { changeLocale } from '../i18n';
import { useTheme } from '../theme/ThemeProvider';
import {
  buildRegisterRequest,
  canSubmitRegister,
  emptyRegisterForm,
  validateRegisterForm,
  withCanton,
  withProvince,
  type RegisterFormValues,
} from './registerForm';

export function RegisterPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { theme, setTheme } = useTheme();
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [values, setValues] = useState<RegisterFormValues>(() =>
    emptyRegisterForm(i18n.language as SupportedLocale, theme),
  );
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const errors = validateRegisterForm(values);
  const canSubmit = canSubmitRegister(values) && !sending;

  const countries = useMemo(() => countriesSortedFor(i18n.language), [i18n.language]);

  /** Los países con su nombre ya traducido, listos para el buscador. */
  const paises = useMemo<CountryOption[]>(
    () =>
      countries.map((country) => ({
        code: country.code,
        name: countryName(country.code, i18n.language),
        flag: country.flag,
      })),
    [countries, i18n.language],
  );

  /** Lo mismo, pero con el prefijo: es lo que se busca al elegir el teléfono. */
  const paisesConPrefijo = useMemo<CountryOption[]>(
    () => paises.map((país) => ({ ...país, dialCode: findCountry(país.code)?.dial ?? '' })),
    [paises],
  );
  const cantons = values.provinceCode === '' ? [] : cantonsOfProvince(values.provinceCode);
  const districts = values.cantonCode === '' ? [] : districtsOfCanton(values.cantonCode);
  const phoneCountry = findCountry(values.whatsappCountry);
  const phoneCountryName = countryName(values.whatsappCountry, i18n.language);
  const nationality = values.nationalityCode === '' ? undefined : findCountry(values.nationalityCode);

  // Un error solo se muestra después de intentar enviar. Marcar en rojo un
  // campo que la persona todavía no llenó es regañarla por ir en orden.
  const shown = (key: keyof typeof errors): string | undefined =>
    submitted && errors[key] !== undefined ? t(errors[key] as string) : undefined;

  const patch = (next: Partial<RegisterFormValues>): void =>
    setValues((current) => ({ ...current, ...next }));

  /** Cambiar de provincia invalida el cantón Y el distrito elegidos antes. */
  const onProvince = (provinceCode: string): void =>
    setValues((current) => withProvince(current, provinceCode));

  const onCanton = (cantonCode: string): void => setValues((current) => withCanton(current, cantonCode));

  const onLocale = (locale: SupportedLocale): void => {
    patch({ locale });
    void changeLocale(locale);
  };

  const onTheme = (next: Theme): void => {
    patch({ theme: next });
    setTheme(next);
  };

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSubmitted(true);
    setServerError(null);
    if (!canSubmitRegister(values)) return;

    setSending(true);
    try {
      // El registro ya devuelve la sesión: se abre acá mismo y la persona
      // entra, igual que después de un login. No hay pantalla intermedia de
      // "revisá tu correo" porque no hay nada que revisar.
      const response = await api.register(buildRegisterRequest(values));

      /*
        LA FOTO VA DESPUÉS Y NO ADENTRO DEL REGISTRO. `POST /profile/photo`
        exige sesión, y durante el formulario todavía no hay cuenta: se
        abre la sesión primero y recién entonces se sube.

        POR ESO LA CUENTA PUEDE QUEDAR SIN FOTO. Si la subida falla —se
        cayó la red entre las dos llamadas— la cuenta YA EXISTE, y
        deshacerla sería peor: la persona tendría que registrarse de nuevo
        con todo el formulario. Se entra igual y la foto se sube desde el
        perfil. `validateRegisterForm` la exige en la pantalla, que es
        donde se puede pedir; el servidor no la exige, y a propósito.
      */
      signIn(response);
      if (values.photo !== null) {
        try {
          await api.uploadPhoto(values.photo);
        } catch {
          // Silencio deliberado: ver el comentario de arriba. La sesión ya
          // está abierta y navegar es lo correcto.
        }
      }
      navigate('/', { replace: true });
    } catch (error) {
      setServerError(errorMessage(t, error));
    } finally {
      setSending(false);
    }
  }

  return (
    <AuthLayout showSwitcher={false}>
      <h1>{t('register.title')}</h1>
      <p className="lead">{t('register.lead')}</p>

      {/*
        QUÉ TENER A MANO, ANTES DE EMPEZAR. Javier: «también avisar que hace
        falta antes de crear cuenta porque la gente está bateada».

        EL PROBLEMA QUE RESUELVE no es que el formulario sea largo, es que
        tiene DOS datos que nadie lleva encima: el número de cédula y el
        distrito. Quien llega sin ellos se entera a la mitad, se va a
        buscarlos, y vuelve a un formulario vacío —la pantalla no guarda nada
        entre visitas—. Listarlos arriba convierte eso en treinta segundos de
        preparación en vez de un abandono.

        SOLO LO QUE ALGUIEN PUEDE NO TENER. El correo y la contraseña no se
        listan: nadie se levanta a buscarlos. Una lista de todo lo que el
        formulario pide sería el formulario otra vez, y no avisaría de nada.

        Y DICE CUÁNTO TARDA. «Unos tres minutos» es lo que decide si alguien
        empieza ahora o lo deja para luego —y «luego» casi siempre es nunca—.
      */}
      <div className="before-start">
        <h2>{t('register.beforeTitle')}</h2>
        <ul>
          <li>{t('register.beforeId')}</li>
          <li>{t('register.beforeLocation')}</li>
          <li>{t('register.beforeWhatsapp')}</li>
        </ul>
        <p className="help">{t('register.beforeTime')}</p>
      </div>
      {serverError !== null && <Alert kind="error">{serverError}</Alert>}

      <form onSubmit={(event) => void onSubmit(event)} noValidate>
        <fieldset>
          <legend>{t('register.section.account')}</legend>

          {/*
            LA FOTO, OPCIONAL Y PRIMERO. `ImagePicker` ofrece los dos caminos
            —galería y `capture="user"`, que en el celular abre la cámara
            directo—; el navegador entrega un archivo en ambos casos.

            OPCIONAL desde que Javier lo pidió: era la única cosa obligatoria
            del formulario que no hace falta para jugar, y era la primera, o
            sea el primer «no puedo seguir» de alguien que todavía no tiene
            cuenta. Sigue PRIMERO porque quien sí la quiere subir la tiene a
            mano, y el archivo viaja en memoria hasta que la cuenta exista.
          */}
          <ImagePicker
            id="photo"
            label={t('register.photo')}
            help={t('register.photoHelp')}
            value={values.photo}
            onChange={(photo) => patch({ photo })}
          />
          {shown('photo') !== undefined && (
            <span className="error" id="photo-error" role="alert">
              {t(shown('photo') as string)}
            </span>
          )}

          <Field id="email" label={t('register.email')} hint={t('common.required')} error={shown('email')}>
            {(control) => (
              <input
                {...control}
                type="email"
                autoComplete="email"
                value={values.email}
                onChange={(e) => patch({ email: e.target.value })}
              />
            )}
          </Field>

          <Field
            id="password"
            label={t('register.password')}
            help={t('register.passwordHelp')}
            hint={t('common.required')}
            error={shown('password')}
          >
            {(control) => (
              <input
                {...control}
                type="password"
                autoComplete="new-password"
                value={values.password}
                onChange={(e) => patch({ password: e.target.value })}
              />
            )}
          </Field>

          <Field
            id="passwordConfirm"
            label={t('register.passwordConfirm')}
            help={t('register.passwordConfirmHelp')}
            hint={t('common.required')}
            error={shown('passwordConfirm')}
          >
            {(control) => (
              <input
                {...control}
                type="password"
                autoComplete="new-password"
                value={values.passwordConfirm}
                onChange={(e) => patch({ passwordConfirm: e.target.value })}
              />
            )}
          </Field>
        </fieldset>

        <fieldset>
          <legend>{t('register.section.identity')}</legend>

          <Field
            id="displayName"
            label={t('register.displayName')}
            help={t('register.displayNameHelp')}
            hint={t('common.required')}
            error={shown('displayName')}
          >
            {(control) => (
              <input
                {...control}
                autoComplete="nickname"
                value={values.displayName}
                onChange={(e) => patch({ displayName: e.target.value })}
              />
            )}
          </Field>

          <Field
            id="fullName"
            // SIN la etiqueta «Privado». La llevaba, y era falso: quien
            // administra un club lo ve en la lista de jugadores y en la
            // búsqueda. Prometer privacidad que no existe es peor que no
            // prometer nada, porque alguien escribe su nombre confiando en eso.
            label={t('register.fullName')}
            help={t('register.fullNameHelp')}
            hint={t('common.required')}
            error={shown('fullName')}
          >
            {(control) => (
              <input
                {...control}
                autoComplete="name"
                value={values.fullName}
                onChange={(e) => patch({ fullName: e.target.value })}
              />
            )}
          </Field>

          {/* El tipo va ANTES que el número: cambia qué se espera abajo. */}
          <ChoiceGroup
            id="idDocumentType"
            legend={t('register.idDocumentType')}
            hint={t('common.required')}
            value={values.idDocumentType}
            options={ID_DOCUMENT_TYPES.map((type) => ({
              value: type,
              label: t(`idDocumentType.${type}`),
            }))}
            onChange={(idDocumentType) => patch({ idDocumentType })}
          />

          <Field
            id="idDocumentNumber"
            label={t('register.idDocumentNumber')}
            help={t(`idDocumentHelp.${values.idDocumentType}`)}
            hint={t('common.required')}
            error={shown('idDocumentNumber')}
          >
            {(control) => (
              <input
                {...control}
                inputMode={values.idDocumentType === 'PASSPORT' ? 'text' : 'numeric'}
                value={values.idDocumentNumber}
                onChange={(e) => patch({ idDocumentNumber: e.target.value })}
              />
            )}
          </Field>

          {/* El sexo va acá y no en «preferencias» porque no es una
              preferencia: decide en qué categorías puede jugar. */}
          <ChoiceGroup
            id="sex"
            legend={t('register.sex')}
            help={t('register.sexHelp')}
            hint={t('common.required')}
            value={values.sex === '' ? null : (values.sex as Sex)}
            options={SEXES.map((sex) => ({ value: sex, label: t(`sex.${sex}`) }))}
            onChange={(sex) => patch({ sex })}
            {...(shown('sex') === undefined ? {} : { error: shown('sex') })}
          />

          <Field
            id="dateOfBirth"
            label={t('register.dateOfBirth')}
            hint={t('common.required')}
            error={shown('dateOfBirth')}
          >
            {(control) => (
              <input
                {...control}
                type="date"
                autoComplete="bday"
                value={values.dateOfBirth}
                onChange={(e) => patch({ dateOfBirth: e.target.value })}
              />
            )}
          </Field>

          <CountryPicker
            id="nationalityCode"
            label={t('register.nationality')}
            help={t('register.nationalityHelp')}
            hint={t('common.required')}
            value={values.nationalityCode}
            options={paises}
            onChange={(nationalityCode) => patch({ nationalityCode })}
            {...(shown('nationalityCode') === undefined
              ? {}
              : { error: shown('nationalityCode') })}
          />
          {nationality !== undefined && (
            <p className="help" data-testid="nationality-preview">
              {/* La bandera nunca sola: decorativa, y el nombre al lado es lo
                  que de verdad identifica al país. */}
              <span aria-hidden="true">{nationality.flag}</span>{' '}
              {countryName(nationality.code, i18n.language)}
            </p>
          )}
        </fieldset>

        <fieldset>
          <legend>{t('register.section.location')}</legend>
          <p className="help">{t('register.locationHelp')}</p>

          <Field
            id="provinceCode"
            label={t('register.province')}
            hint={t('common.required')}
            error={shown('provinceCode')}
          >
            {(control) => (
              <Select
                {...control}
                value={values.provinceCode}
                // La opción vacía es una opción de verdad y no un texto de
                // relleno: borrar la provincia arrastra el cantón y el
                // distrito, y sin ella no habría forma de deshacer.
                options={[
                  { value: '', label: t('common.selectPlaceholder') },
                  ...PROVINCES.map((province) => ({ value: province.code, label: province.name })),
                ]}
                onChange={onProvince}
              />
            )}
          </Field>

          <Field
            id="cantonCode"
            label={t('register.canton')}
            help={values.provinceCode === '' ? t('register.cantonDisabled') : undefined}
            hint={t('common.required')}
            error={shown('cantonCode')}
          >
            {(control) => (
              <Select
                {...control}
                value={values.cantonCode}
                disabled={values.provinceCode === ''}
                options={[
                  { value: '', label: t('common.selectPlaceholder') },
                  ...cantons.map((canton) => ({ value: canton.code, label: canton.name })),
                ]}
                onChange={onCanton}
              />
            )}
          </Field>

          <Field
            id="districtCode"
            label={t('register.district')}
            help={values.cantonCode === '' ? t('register.districtDisabled') : undefined}
            hint={t('common.required')}
            error={shown('districtCode')}
          >
            {(control) => (
              <Select
                {...control}
                value={values.districtCode}
                disabled={values.cantonCode === ''}
                options={[
                  { value: '', label: t('common.selectPlaceholder') },
                  ...districts.map((district) => ({ value: district.code, label: district.name })),
                ]}
                onChange={(districtCode) => patch({ districtCode })}
              />
            )}
          </Field>

          <Field
            id="addressLine"
            label={t('register.addressLine')}
            help={t('register.addressLineHelp')}
            hint={t('common.optional')}
          >
            {(control) => (
              <input
                {...control}
                autoComplete="street-address"
                value={values.addressLine}
                onChange={(e) => patch({ addressLine: e.target.value })}
              />
            )}
          </Field>
        </fieldset>

        <fieldset>
          <legend>{t('register.section.contact')}</legend>
          <p className="help">{t('register.whatsappHelp')}</p>

          {/* Los dos campos son hijos DIRECTOS de .phone, sin divs de por
              medio: la fila define los renglones (etiqueta, ayuda, control,
              error) y cada campo se cuelga de ellos. Un envoltorio en el
              medio corta esa cadena y los controles se vuelven a desalinear. */}
          <div className="phone">
            <CountryPicker
              id="whatsappCountry"
              label={t('register.whatsappCountry')}
              hint={t('common.required')}
              value={values.whatsappCountry}
              options={paisesConPrefijo}
              onChange={(whatsappCountry) => patch({ whatsappCountry })}
            />
            <Field
              id="whatsappNumber"
              label={t('register.whatsappNumber')}
              help={
                phoneCountry?.example === undefined
                  ? t('register.whatsappGeneric', { country: phoneCountryName })
                  : t('register.whatsappExample', {
                      country: phoneCountryName,
                      example: phoneCountry.example,
                    })
              }
              hint={t('common.required')}
              error={shown('whatsappNumber')}
            >
              {(control) => (
                <input
                  {...control}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel-national"
                  value={values.whatsappNumber}
                  onChange={(e) => patch({ whatsappNumber: e.target.value })}
                />
              )}
            </Field>
          </div>
        </fieldset>

        <fieldset>
          <legend>{t('register.section.play')}</legend>

          {/*
            LA CATEGORÍA, ACÁ Y NO DESPUÉS. Es lo que decide en qué categoría
            entra a un club, y perseguir a alguien para que complete un dato
            que ya podía haber elegido es cómo ese dato se queda vacío para
            siempre.

            OBLIGATORIA, por pedido de Javier. Fue opcional con este
            argumento, que se deja escrito porque sigue siendo cierto: quien
            recién empieza no sabe en qué nivel está, y obligarlo a elegir
            mete un dato inventado que después nadie revisa. Del otro lado:
            sin nivel no se aparece en ninguna búsqueda por categoría. El
            desplegable arranca vacío a propósito, para que nadie se registre
            por inercia con el primero de la lista.
          */}
          <Field
            id="declaredLevel"
            label={t('register.level')}
            help={t('register.levelHelp')}
            hint={t('common.required')}
            error={shown('declaredLevel')}
          >
            {(control) => (
              <Select
                {...control}
                value={values.declaredLevel}
                options={[
                  { value: '', label: t('register.levelPlaceholder') },
                  ...CATEGORY_LEVELS.map((level) => ({
                    value: level,
                    label: t(`categoryLevel.${level}`),
                  })),
                ]}
                onChange={(declaredLevel) =>
                  patch({ declaredLevel: declaredLevel as RegisterFormValues['declaredLevel'] })
                }
              />
            )}
          </Field>

          <UbicacionDelRegistro values={values} patch={patch} />

          <div className="field checkbox">
            <input
              id="availableForChallenges"
              type="checkbox"
              checked={values.availableForChallenges}
              onChange={(e) => patch({ availableForChallenges: e.target.checked })}
              aria-describedby="availableForChallenges-help"
            />
            <div>
              <label htmlFor="availableForChallenges">{t('register.available')}</label>
              <span className="help" id="availableForChallenges-help">
                {t('register.availableHelp')}
              </span>
            </div>
          </div>

          {/* Los avisos por correo, tildado de fábrica. Se pregunta acá y no
              solo en «Más» porque antes se forzaba a `true` en el servidor y
              la persona se enteraba meses después, recibiendo correo que
              nunca aceptó. No apaga la campanita: ver el contrato. */}
          <div className="field checkbox">
            <input
              id="emailNotifications"
              type="checkbox"
              checked={values.emailNotifications}
              onChange={(e) => patch({ emailNotifications: e.target.checked })}
              aria-describedby="emailNotifications-help"
            />
            <div>
              <label htmlFor="emailNotifications">{t('register.emailNotifications')}</label>
              <span className="help" id="emailNotifications-help">
                {t('register.emailNotificationsHelp')}
              </span>
            </div>
          </div>

          <div className="row">
            <Field id="locale" label={t('register.locale')}>
              {(control) => (
                <Select
                  {...control}
                  value={values.locale}
                  options={SUPPORTED_LOCALES.map((locale) => ({
                    value: locale,
                    label: t(`language.${locale}`),
                  }))}
                  onChange={onLocale}
                />
              )}
            </Field>

            <Field id="theme" label={t('register.theme')}>
              {(control) => (
                <Select
                  {...control}
                  value={values.theme}
                  options={THEMES.map((value) => ({ value, label: t(`theme.${value}`) }))}
                  onChange={onTheme}
                />
              )}
            </Field>
          </div>
        </fieldset>

        <button type="submit" disabled={!canSubmit}>
          {sending ? t('common.sending') : t('register.submit')}
        </button>
      </form>

      <div className="links">
        <span>
          {t('register.haveAccount')} <Link to="/login">{t('register.login')}</Link>
        </span>
      </div>
    </AuthLayout>
  );
}

/**
 * LA UBICACIÓN, EN EL REGISTRO Y OPCIONAL.
 *
 * POR QUÉ ACÁ. Quien se registra sin ubicación NO APARECE en la búsqueda de
 * retos —el producto entero se apoya en la distancia— y no tiene forma de
 * saberlo. Este es el único momento en que esa persona está mirando esta
 * pregunta.
 *
 * LA ADVERTENCIA NO ES UN ADORNO. Se guarda DÓNDE ESTÁ AHORA, y quien se
 * registra en la fila del banco queda buscando retos en el centro. Decirlo
 * antes del botón es la diferencia entre un dato útil y uno que hay que
 * corregir después — si es que alguien se entera de que hay algo que
 * corregir.
 *
 * SE PIDE DE UN BOTÓN, nunca sola: un cartel del navegador que aparece sin
 * que nadie haya pedido nada se cierra sin leerlo, y después ya no se puede
 * volver a preguntar.
 */
function UbicacionDelRegistro({
  values,
  patch,
}: {
  values: RegisterFormValues;
  patch: (next: Partial<RegisterFormValues>) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const aparato = useDeviceLocation();

  return (
    <div className="field">
      <span className="field-label">
        {t('register.gpsLocation')}
        <span className="help"> ({t('common.optional')})</span>
      </span>
      <span className="help">{t('register.gpsLocationHelp')}</span>

      {aparato.error !== null && <Alert kind="error">{aparato.error}</Alert>}

      {values.location !== null && (
        <p className="location-current">
          <PinIcon title={t('location.title')} />
          {/* Se muestran: es el dato de la persona y tiene derecho a ver
              exactamente qué se va a guardar. */}
          <span>
            {values.location.latitude.toFixed(3)}, {values.location.longitude.toFixed(3)}
          </span>
        </p>
      )}

      <div className="button-row">
        <button
          type="button"
          className="link"
          disabled={aparato.busy}
          onClick={() => aparato.pedir((location) => patch({ location }))}
        >
          {aparato.busy
            ? t('location.asking')
            : values.location !== null
              ? t('location.update')
              : t('location.use')}
        </button>
        {values.location !== null && (
          <button type="button" className="link" onClick={() => patch({ location: null })}>
            {t('location.remove')}
          </button>
        )}
      </div>
    </div>
  );
}
